import { toast } from '@ixigo/iui/Toast';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@/lib/navigation';
import { redirectToPayment } from '@/utils/processPayment';
import type {
	RescheduleCabOption,
	RescheduleFareData,
} from '../../../-api/reschedule';
import {
	confirmReschedule,
	getRescheduleFareDetails,
} from '../../../-api/reschedule';
import type { RescheduleError, RescheduleStep } from '../index.types';

interface UseRescheduleActionsOptions {
	bookingId: string;
	initialPickupDateTime: string;
	dateTime: string;
	selectedCab: RescheduleCabOption | null;
	transitions: {
		startLoading: (target: 'loading-cabs' | 'processing') => void;
		setFareData: (data: RescheduleFareData) => void;
		selectCab: (cabId: string) => void;
		failWith: (error: RescheduleError) => void;
		resetFlow: () => void;
	};
	abort: {
		createAbortSignal: () => AbortSignal;
	};
	onModalClosed: () => void;
	onRouterOperationStart: () => void;
}

export function useRescheduleActions({
	bookingId,
	initialPickupDateTime,
	dateTime,
	selectedCab,
	transitions,
	abort,
	onModalClosed,
	onRouterOperationStart,
}: UseRescheduleActionsOptions) {
	const { startLoading, setFareData, selectCab, failWith, resetFlow } =
		transitions;
	const { createAbortSignal } = abort;

	const router = useRouter();
	const navigate = useNavigate();
	const exitCallbackRef = useRef<(() => void) | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryStepRef = useRef<RescheduleStep>('form');

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleModalExited = useCallback(() => {
		exitCallbackRef.current?.();
		exitCallbackRef.current = null;
		resetFlow();
	}, [resetFlow]);

	const handleContinue = useCallback(
		async (newDateTime?: string) => {
			const effectiveDateTime = newDateTime || dateTime;

			// Check if user selected the same pickup time as the original booking
			const isSameAsOriginalPickupTime =
				new Date(effectiveDateTime).getTime() ===
				new Date(initialPickupDateTime).getTime();

			if (isSameAsOriginalPickupTime) {
				toast.error('Please select a different date and time', {
					duration: 2000,
					containerStyle: { bottom: '90px' },
				});
				return;
			}

			startLoading('loading-cabs');
			retryStepRef.current = 'loading-cabs';
			const signal = createAbortSignal();

			const response = await getRescheduleFareDetails(
				bookingId,
				effectiveDateTime,
				{
					signal,
				},
			);

			if (signal.aborted) return;

			if ('data' in response) {
				setFareData(response.data);
			} else {
				failWith({
					type: response.errors.type,
					title: response.errors.title,
					message: response.errors.message,
				});
			}
		},
		[
			bookingId,
			dateTime,
			initialPickupDateTime,
			startLoading,
			createAbortSignal,
			setFareData,
			failWith,
		],
	);

	const handleCabConfirm = useCallback(
		(cabId: string) => {
			selectCab(cabId);
		},
		[selectCab],
	);

	const handleAmountAction = useCallback(
		async (burnOpted?: boolean) => {
			if (!selectedCab) return;

			startLoading('processing');
			retryStepRef.current = 'processing';
			const signal = createAbortSignal();

			const response = await confirmReschedule(
				bookingId,
				selectedCab.cabSessionId,
				dateTime,
				burnOpted,
				{
					signal,
				},
			);

			if (signal.aborted) return;

			if ('data' in response) {
				const { bookingId: newBookingId, paymentDetails } = response.data;
				if (paymentDetails) {
					redirectToPayment({
						bookingId: newBookingId,
						paymentDetails,
					});
					setTimeout(() => {
						router.invalidate();
						onModalClosed();
					}, 5000);
				} else {
					onRouterOperationStart();
					navigate({
						to: '/trips/$bookingId',
						params: { bookingId: newBookingId },
						search: { confirmation: true },
						replace: true,
						viewTransition: true,
					}).then(() => {
						window.scrollTo({ top: 0 });
					});
					exitCallbackRef.current = () => {
						onModalClosed();
					};
				}
			} else {
				failWith({
					type: response.errors.type,
					title: response.errors.title,
					message: response.errors.message,
				});
			}
		},
		[
			bookingId,
			dateTime,
			selectedCab,
			onRouterOperationStart,
			onModalClosed,
			router,
			navigate,
			startLoading,
			createAbortSignal,
			failWith,
		],
	);

	const handleTryAgain = useCallback(() => {
		if (retryStepRef.current === 'loading-cabs') {
			handleContinue();
		} else {
			handleAmountAction();
		}
	}, [handleContinue, handleAmountAction]);

	const handleGetHelp = useCallback(() => {
		navigate({
			to: '/support',
			search: { bookingId },
			resetScroll: true,
		});
	}, [bookingId, navigate]);

	return {
		handleContinue,
		handleCabConfirm,
		handleAmountAction,
		handleTryAgain,
		handleGetHelp,
		handleModalExited,
	};
}
